import { useState } from "react";
import { twMerge } from "tailwind-merge";

import Icons from "assets";
import { IconButton } from "ui/icon-button";
import { SidebarMenu, type SidebarMenuItem, type SidebarMenuSection } from "components/partials/sidebar-menu";

import { COLLEGE_PROFILE_NAV_ITEMS, type CollegeProfileSectionId } from "containers/admin/clients/client/college-profile-page/college-profile-page.types";

export type CollegeProfileNavProps = {
  activeSectionId: CollegeProfileSectionId;
  onSectionSelect: (sectionId: CollegeProfileSectionId) => void;
};

/**
 * Left nav for the College profile page. Visually mirrors `SchemaSidebar`
 * (collapsible icon rail + `SidebarMenu.Items`) but is a fresh, page-local
 * component driven by a hardcoded section list rather than a JSON schema.
 */
const CollegeProfileNav = ({ activeSectionId, onSectionSelect }: CollegeProfileNavProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const sections: SidebarMenuSection[] = [
    {
      id: "college-profile-nav",
      items: COLLEGE_PROFILE_NAV_ITEMS.map((item) =>
        "children" in item
          ? {
              id: item.id,
              label: item.label,
              children: item.children.map((childItem) => ({
                id: childItem.id,
                label: childItem.label,
                onSelect: () => onSectionSelect(childItem.id),
              })),
            }
          : {
              id: item.id,
              label: item.label,
              onSelect: () => onSectionSelect(item.id),
            },
      ),
    },
  ];

  const handleItemSelect = (item: SidebarMenuItem) => {
    onSectionSelect(item.id as CollegeProfileSectionId);
  };

  return (
    <SidebarMenu isCollapsed={isCollapsed} itemLabelSize="sm" className="shrink-0">
      <div className={twMerge("pt-4 pb-1", isCollapsed ? "flex justify-center px-3" : "px-4")}>
        <IconButton
          color="inherit"
          variant="text"
          size="small"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <Icons.Sidebar />
        </IconButton>
      </div>
      {!isCollapsed && (
        <SidebarMenu.Items
          sections={sections}
          activeItemId={activeSectionId}
          onItemSelect={handleItemSelect}
          className="py-3"
        />
      )}
    </SidebarMenu>
  );
};

export default CollegeProfileNav;
